/**
 * Credentials in URLs and headers never reach Sentry.
 *
 * Sign-in links carry their credential in the URL: `?user=&expires=&token=`
 * (UserEntity.generateUrl, read by lib/auth.ts user_id_url: email links,
 * password resets, demo links), and every route whose path IS its credential
 * (TOKEN_PATH_PREFIXES: a text's `/s/<token>`, a campaign's `/r/<token>`
 * click, `/u/<token>` unsubscribe (valid ten years), `/wb/<token>` win-back). Other URLs carry keys and signatures (OAuth
 * `code`/`state`, presigned storage URLs, `?key=`, download `&t=` tokens).
 * Any of them can land in an error's `request`, a breadcrumb's fetch or
 * navigation URL, a transaction or span name, a span's `url.full`, or a
 * Session Replay recording. Every hook Sentry offers (beforeSend,
 * beforeSendTransaction, beforeSendSpan, beforeBreadcrumb, an event
 * processor, Replay's beforeAddRecordingEvent) runs `scrub_sentry` on what it
 * is handed, which:
 *
 *  - in every string: replaces the value of a sensitive query parameter
 *    (SENSITIVE_PARAMS, plain or percent-encoded inside another URL) with
 *    `[Filtered]`, a `/s/<token>` (or `/r/`, `/u/`, `/wb/`) path token with
 *    `/s/:token`, and a
 *    `Bearer <token>` or `Basic <base64>` with `Bearer [Filtered]`;
 *  - for every key that names a credential (Authorization, Cookie,
 *    Set-Cookie, `*-token`, `*-api-key`, `*-secret`, `*-signature`,
 *    `password`, `session-key`, …, as a header, a
 *    span attribute such as `http.request.header.cookie`, or a recorded DOM
 *    attribute): replaces the whole value with `[Filtered]`;
 *  - for a `[name, value]` pair (Sentry's array form of `query_string`) whose
 *    name is a sensitive parameter: replaces the value.
 *
 * Pure and import-free: the browser bundle runs it too. It only ever removes
 * data that is about to be sent to Sentry; nothing here is shown to anyone.
 */

import { scrub_sms_link_tokens } from '~/lib/sms-link-scrub';

export const FILTERED = '[Filtered]';

/** Query parameters whose value is a credential (case-insensitive, exact name). */
export const SENSITIVE_PARAMS = [
  'token',
  't',
  'code',
  'code_verifier',
  'state',
  'sig',
  'signature',
  'exp',
  'expires',
  'key',
  'api_key',
  'apikey',
  'access_token',
  'refresh_token',
  'id_token',
  'auth',
  'jwt',
  'otp',
  'secret',
  'client_secret',
  'password',
  'session_key',
  'x-amz-signature',
  'x-amz-credential',
  'x-amz-security-token',
] as const;

const PARAM_SET = new Set<string>(SENSITIVE_PARAMS);

const PARAM_NAMES = SENSITIVE_PARAMS.map((name) => name.replace(/[-]/g, '\\-')).join('|');

/** `?token=abc`, `&code=…`, `#access_token=…`: the value runs to the next `&`, `#` or URL end. */
const PARAM_PLAIN = new RegExp(`([?&#;])(${PARAM_NAMES})=([^&#\\s"'<>\\\\]*)`, 'gi');

/** The same, percent-encoded inside another URL: `%3Ftoken%3Dabc%26…`. */
const PARAM_ENCODED = new RegExp(`(%3F|%26|%23)(${PARAM_NAMES})(%3D)((?:(?!%26|%23)[^&#\\s"'<>\\\\])*)`, 'gi');

/**
 * Routes whose path segment after the prefix IS the credential, so the whole
 * URL is a key: `/s/<token>` (a text's sign-in link, _auth+/s.$token.tsx),
 * `/r/<token>` (campaign click redirect, r.$token.tsx), `/u/<token>` (one-click
 * unsubscribe, valid ten years, u.$token.tsx), `/wb/<token>` (win-back accept,
 * wb.$token.tsx), `/welcome/<token>` (a photographer's single-use set-password
 * link, welcome.$token.tsx). A new tokenized route belongs here.
 */
export const TOKEN_PATH_PREFIXES = ['s', 'r', 'u', 'wb', 'welcome'] as const;

const TOKEN_PREFIXES = TOKEN_PATH_PREFIXES.join('|');

/**
 * A `/<prefix>/<token>` path as the first segment (at the start of a string,
 * after a character a path cannot continue from, or right after
 * `//host[:port]`) whose token is the LAST segment. Wider than the text-link
 * scrubber (lib/sms-link-scrub.ts, which only matches a token in exactly the
 * issued format): any token-like segment goes. Another site's deeper path
 * (fonts.gstatic.com/s/manrope/v15/…) is left alone, as its segment is
 * followed by `/`.
 */
const SLINK_PLAIN = new RegExp(
  `(^|[\\s"'(<=,]|//[A-Za-z0-9.-]+(?::\\d+)?)/(${TOKEN_PREFIXES})/[A-Za-z0-9_.~%-]{3,}(?=$|[?#&\\s"'<>),\\\\])`,
  'gi',
);

/** The same, percent-encoded inside another URL: `%2F%2Fhost%2Fs%2F<token>`. */
const SLINK_ENCODED = new RegExp(
  `(%2F%2F[A-Za-z0-9.-]+(?:%3A\\d+)?)%2F(${TOKEN_PREFIXES})%2F[A-Za-z0-9_.~-]{3,}(?=$|%3F|%23|%26|[?#&\\s"'<>),\\\\])`,
  'gi',
);

/** Cheap pre-check: any `/<prefix>/`, plain or encoded. */
const SLINK_HINT = new RegExp(`(\\/|%2F)(${TOKEN_PREFIXES})(\\/|%2F)`, 'i');

/** An Authorization header's value quoted in a message: `Bearer <token>`, `Basic <base64>`. */
const BEARER = /\b(Bearer)\s+[A-Za-z0-9._~+/-]{8,}=*|\b(Basic)\s+[A-Za-z0-9+/]{12,}=*/g;

/**
 * Keys whose whole value is a credential: headers, span attributes
 * (`http.request.header.authorization`), cookies, form fields, recorded DOM
 * attributes (`session-key`). Matched on the key's last `.`-separated part.
 */
const SENSITIVE_KEY =
  /^(authorization|proxy-authorization|cookie|cookies|set-cookie|passwd|x-auth|(?:[a-z0-9_-]*[-_])?(?:api[-_]?key|(?:session|secret|private|access|auth|subscription|functions|master|signing|encryption|webhook)[-_]?key|password|token|secret|signature(?:[-_]\d+)?|hmac(?:[-_]sha\d+)?))$/i;

function key_sensitive(key: string): boolean {
  const last = key.slice(key.lastIndexOf('.') + 1);

  return SENSITIVE_KEY.test(last);
}

function param_sensitive(name: string): boolean {
  return PARAM_SET.has(name.toLowerCase());
}

/** `text` with every credential in a URL (query value, `/s/`-style path token) or auth header value replaced. */
export function scrub_sentry_string(text: string): string {
  let out = scrub_sms_link_tokens(text);

  if (/[?&#;]|%3F|%26|%23/i.test(out)) {
    out = out
      .replace(PARAM_PLAIN, (_m, before: string, name: string) => `${before}${name}=${FILTERED}`)
      .replace(PARAM_ENCODED, (_m, before: string, name: string, eq: string) => `${before}${name}${eq}${FILTERED}`);
  }

  if (SLINK_HINT.test(out)) {
    out = out
      .replace(SLINK_PLAIN, (_m, before: string, prefix: string) => `${before}/${prefix}/:token`)
      .replace(SLINK_ENCODED, (_m, before: string, prefix: string) => `${before}%2F${prefix}%2F:token`);
  }

  if (/Bearer|Basic/.test(out)) {
    out = out.replace(BEARER, (_m, bearer?: string, basic?: string) => `${bearer ?? basic} ${FILTERED}`);
  }

  return out;
}

/** A bare query string (`token=abc&x=1`, no leading `?`), scrubbed. */
export function scrub_sentry_query_string(query: string): string {
  return scrub_sentry_string(`?${query}`).slice(1);
}

/**
 * `value` with every credential in it scrubbed, in place (objects and arrays
 * are walked; anything else is returned as it is). For a Sentry event,
 * transaction, span or breadcrumb, or a replay recording event.
 */
export function scrub_sentry<T>(value: T): T {
  if (value && typeof value === 'object') {
    const request = (value as { request?: unknown }).request;

    if (request && typeof request === 'object') {
      scrub_request(request as Record<string, unknown>);
    }
  }

  return walk(value, 0, new WeakSet(), scrub_sentry_string) as T;
}

/**
 * Query parameters a Session Replay recording drops too: personal data in a
 * page or fetch URL (an address typed into a search, a map link's query, an
 * email or phone), which the recording's network and navigation entries carry
 * as text that text masking does not reach.
 */
export const REPLAY_PERSONAL_PARAMS = [
  'address',
  'street',
  'zip',
  'postal_code',
  'q',
  'query',
  'input',
  'search',
  'destination',
  'daddr',
  'saddr',
  'origin',
  'latlng',
  'location',
  'email',
  'phone',
  'name',
  'first_name',
  'last_name',
] as const;

const PERSONAL_NAMES = REPLAY_PERSONAL_PARAMS.join('|');

const PERSONAL_PLAIN = new RegExp(`([?&#;])(${PERSONAL_NAMES})=([^&#\\s"'<>\\\\]*)`, 'gi');

const PERSONAL_ENCODED = new RegExp(`(%3F|%26|%23)(${PERSONAL_NAMES})(%3D)((?:(?!%26|%23)[^&#\\s"'<>\\\\])*)`, 'gi');

/** `mailto:`, `tel:` and `sms:` links: the address or number after the scheme. */
const CONTACT_LINK = /\b(mailto|tel|sms|callto):[^\s"'<>]+/gi;

/** A string in a replay recording: credentials, then personal data in URLs and contact links. */
export function scrub_replay_string(text: string): string {
  let out = scrub_sentry_string(text);

  if (/[?&#;]|%3F|%26|%23/i.test(out)) {
    out = out
      .replace(PERSONAL_PLAIN, (_m, before: string, name: string) => `${before}${name}=${FILTERED}`)
      .replace(PERSONAL_ENCODED, (_m, before: string, name: string, eq: string) => `${before}${name}${eq}${FILTERED}`);
  }

  if (/(mailto|tel|sms|callto):/i.test(out)) {
    out = out.replace(CONTACT_LINK, (_m, scheme: string) => `${scheme}:${FILTERED}`);
  }

  return out;
}

/**
 * A Session Replay recording event (Replay's beforeAddRecordingEvent), in
 * place: everything `scrub_sentry` drops, and personal data in the URLs and
 * links it recorded. Masking the page itself is the Replay config's job
 * (lib/sentry-replay-privacy.ts).
 */
export function scrub_replay_recording<T>(value: T): T {
  return walk(value, 0, new WeakSet(), scrub_replay_string) as T;
}

/** An event's `request`: its query string and body in their non-URL forms. */
function scrub_request(request: Record<string, unknown>) {
  for (const field of ['query_string', 'data'] as const) {
    const value = request[field];

    if (typeof value === 'string') {
      request[field] = scrub_sentry_query_string(value);
    } else if (value && typeof value === 'object' && !Array.isArray(value)) {
      const record = value as Record<string, unknown>;

      for (const key of Object.keys(record)) {
        if (param_sensitive(key)) {
          record[key] = FILTERED;
        }
      }
    }
  }
}

function walk(value: unknown, depth: number, seen: WeakSet<object>, str: (text: string) => string): unknown {
  if (typeof value === 'string') {
    return str(value);
  }

  if (value === null || typeof value !== 'object' || depth > 24 || seen.has(value)) {
    return value;
  }

  seen.add(value);

  if (Array.isArray(value)) {
    // `[name, value]`: Sentry's pair form of a query string or headers.
    if (value.length === 2 && typeof value[0] === 'string' && typeof value[1] === 'string') {
      if (param_sensitive(value[0]) || key_sensitive(value[0])) {
        value[1] = FILTERED;

        return value;
      }
    }

    for (let i = 0; i < value.length; i++) {
      const next = walk(value[i], depth + 1, seen, str);

      if (next !== value[i]) {
        value[i] = next;
      }
    }

    return value;
  }

  const record = value as Record<string, unknown>;

  for (const key of Object.keys(record)) {
    const current = record[key];
    const next = current !== null && current !== undefined && key_sensitive(key) ? FILTERED : walk(current, depth + 1, seen, str);

    if (next !== current) {
      try {
        record[key] = next;
      } catch {
        // A read-only field: left as it is.
      }
    }
  }

  return value;
}
